import { useQuery, useMutation } from "@tanstack/react-query";
import api, { getImageUrl } from "../../services/api";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Swal from "sweetalert2";

export default function Products() {
  const [downloading, setDownloading] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const queryClient = useQueryClient();

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1); // Reset to page 1 when search changes
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch products with pagination and search
  const { data, isLoading, error } = useQuery({
    queryKey: ["products", debouncedSearch, currentPage],
    queryFn: async () => {
      if (debouncedSearch.trim()) {
        // Search endpoint
        const response = await api.get(`/products/search?q=${encodeURIComponent(debouncedSearch)}&page=${currentPage}&limit=20`);
        return response.data;
      } else {
        // List endpoint with pagination
        const response = await api.get(`/products?page=${currentPage}&limit=20`);
        return response.data;
      }
    },
  });

  const products = data?.products || [];
  const pagination = data?.pagination;

  const deleteMutation = useMutation({
    mutationFn: (productId) => api.delete(`/products/${productId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      Swal.fire({
        icon: "success",
        title: "Product Deleted!",
        text: "The product has been deleted successfully.",
        confirmButtonColor: "#6366f1",
        timer: 2000,
        showConfirmButton: true,
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: error?.response?.data?.message || "Failed to delete product. Please try again.",
        confirmButtonColor: "#6366f1",
      });
    },
  });

  const handleDelete = async (productId, productName) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      html: `Do you want to delete <strong>"${productName}"</strong>?<br><br>This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      showConfirmButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      buttonsStyling: true,
      reverseButtons: false,
      allowOutsideClick: true,
      allowEscapeKey: true,
      width: "500px",
      padding: "2rem",
      customClass: {
        popup: "swal2-popup-custom",
        actions: "swal2-actions-custom",
        confirmButton: "swal2-confirm-custom",
        cancelButton: "swal2-cancel-custom",
      },
      didOpen: () => {
        const confirmBtn = document.querySelector('.swal2-confirm');
        const cancelBtn = document.querySelector('.swal2-cancel');
        if (confirmBtn) {
          confirmBtn.style.display = 'inline-block';
          confirmBtn.style.visibility = 'visible';
          confirmBtn.style.opacity = '1';
        }
        if (cancelBtn) {
          cancelBtn.style.display = 'inline-block';
          cancelBtn.style.visibility = 'visible';
          cancelBtn.style.opacity = '1';
        }
      },
    });

    if (result.isConfirmed) {
      deleteMutation.mutate(productId);
    }
  };

  const handleDownloadQR = async (productId, productSlug, productName) => {
    try {
      setDownloading(productId);
      const token = localStorage.getItem("token");
      
      const response = await fetch(
        `https://backend.reviewscanandgo.com/api/products/${productSlug}/qr/download`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download QR code");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `qr-code-${productName.replace(/\s+/g, "-").toLowerCase()}-${productId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading QR code:", error);
      alert("Failed to download QR code. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) return (
    <div className="text-center py-12 text-gray-600">
      Loading products<span className="animate-pulse">...</span>
    </div>
  );

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          Error loading products: {error?.response?.data?.message || "Something went wrong"}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-8 mb-6">
      <div className="flex justify-between items-center mb-4 md:mb-6 pb-4 md:pb-6 border-b border-gray-200 flex-wrap gap-3 md:gap-4">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">Products</h2>
        <Link
          to="/admin/products/new"
          className="bg-gradient-to-r from-primary to-secondary text-white px-4 md:px-6 py-2 md:py-3 rounded-lg text-sm md:text-base font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
        >
          + Add Product
        </Link>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <input
            type="text"
            placeholder="Search by name or ASIN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2.5 pl-10 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {debouncedSearch && (
          <p className="text-sm text-gray-500 mt-2">
            Searching for: <span className="font-semibold">"{debouncedSearch}"</span>
            {pagination && ` (${pagination.total} result${pagination.total !== 1 ? 's' : ''})`}
          </p>
        )}
      </div>

      {products.length > 0 ? (
        <>
          {/* Desktop Table */}
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full border-collapse min-w-[600px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200 w-16">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200">ASIN</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200">Image</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p, index) => {
                  const rowNumber = pagination 
                    ? (pagination.current_page - 1) * pagination.per_page + index + 1
                    : index + 1;
                  return (
                    <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 text-gray-600 border-b border-gray-200 font-medium">{rowNumber}</td>
                      <td className="px-4 py-4 font-medium text-gray-900 border-b border-gray-200">{p.name}</td>
                      <td className="px-4 py-4 font-mono text-sm text-gray-600 border-b border-gray-200">{p.asin}</td>
                      <td className="px-4 py-4 border-b border-gray-200">
                        {p.image && (
                          <img
                            src={getImageUrl(p.image)}
                            alt={p.name}
                            className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                          />
                        )}
                      </td>
                      <td className="px-4 py-4 border-b border-gray-200">
                        <div className="flex gap-1.5 items-center">
                          <Link
                            to={`/admin/products/${p.id}/edit`}
                            className="inline-flex items-center justify-center w-9 h-9 p-0 border-2 border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all hover:-translate-y-0.5"
                            title="Edit Product"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </Link>
                          <button
                            onClick={() => handleDownloadQR(p.id, p.slug, p.name)}
                            className="inline-flex items-center justify-center w-9 h-9 p-0 border-0 rounded-lg bg-gradient-to-r from-primary to-secondary text-white hover:shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                            disabled={downloading === p.id}
                            title="Download QR Code"
                          >
                            {downloading === p.id ? (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin">
                                <circle cx="12" cy="12" r="10"></circle>
                                <path d="M12 6v6l4 2"></path>
                              </svg>
                            ) : (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => handleDelete(p.id, p.name)}
                            className="inline-flex items-center justify-center w-9 h-9 p-0 border-2 border-red-300 rounded-lg bg-white text-red-600 hover:bg-red-50 hover:border-red-400 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                            disabled={deleteMutation.isPending}
                            title="Delete Product"
                          >
                            {deleteMutation.isPending ? (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin">
                                <circle cx="12" cy="12" r="10"></circle>
                                <path d="M12 6v6l4 2"></path>
                              </svg>
                            ) : (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="block md:hidden space-y-3">
            {products.map((p, index) => {
              const rowNumber = pagination 
                ? (pagination.current_page - 1) * pagination.per_page + index + 1
                : index + 1;
              return (
              <div key={p.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-gray-200 rounded-full text-sm font-semibold text-gray-700">
                    {rowNumber}
                  </div>
                  {p.image && (
                    <img
                      src={getImageUrl(p.image)}
                      alt={p.name}
                      className="w-16 h-16 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1.5 break-words line-clamp-2">{p.name}</h3>
                    <div className="text-xs text-gray-500 font-mono">ASIN: {p.asin}</div>
                  </div>
                </div>
                <div className="flex gap-2 pt-3 border-t border-gray-200">
                  <Link
                    to={`/admin/products/${p.id}/edit`}
                    className="flex-1 inline-flex items-center justify-center h-10 border-2 border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition-all"
                    title="Edit Product"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                    <span className="ml-1.5 text-xs font-medium">Edit</span>
                  </Link>
                  <button
                    onClick={() => handleDownloadQR(p.id, p.slug, p.name)}
                    className="flex-1 inline-flex items-center justify-center h-10 border-0 rounded-lg bg-gradient-to-r from-primary to-secondary text-white hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={downloading === p.id}
                    title="Download QR Code"
                  >
                    {downloading === p.id ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 6v6l4 2"></path>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                    )}
                    <span className="ml-1.5 text-xs font-medium">QR</span>
                  </button>
                  <button
                    onClick={() => handleDelete(p.id, p.name)}
                    className="flex-1 inline-flex items-center justify-center h-10 border-2 border-red-300 rounded-lg bg-white text-red-600 hover:bg-red-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={deleteMutation.isPending}
                    title="Delete Product"
                  >
                    {deleteMutation.isPending ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 6v6l4 2"></path>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    )}
                    <span className="ml-1.5 text-xs font-medium">Delete</span>
                  </button>
                </div>
              </div>
            );
            })}
          </div>

          {/* Pagination Controls */}
          {pagination && pagination.total_pages > 1 && (
            <div className="mt-6 pt-6 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-600">
                Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} products
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={!pagination.has_prev}
                  className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.total_pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.current_page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.current_page >= pagination.total_pages - 2) {
                      pageNum = pagination.total_pages - 4 + i;
                    } else {
                      pageNum = pagination.current_page - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 min-w-[40px] rounded-lg transition-colors ${
                          pageNum === pagination.current_page
                            ? "bg-primary text-white"
                            : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={!pagination.has_next}
                  className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-16 px-4">
          <div className="w-20 h-20 mx-auto mb-6 opacity-40 text-gray-400">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-full h-full">
              <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            {debouncedSearch ? "No products found" : "No products yet"}
          </h3>
          <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
            {debouncedSearch 
              ? `No products match your search "${debouncedSearch}"`
              : "Get started by adding your first product to the system"}
          </p>
          {!debouncedSearch && (
            <Link
              to="/admin/products/new"
              className="inline-block bg-gradient-to-r from-primary to-secondary text-white px-6 py-3 rounded-lg font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              Add Your First Product
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
